import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Bug, 
  Plus, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  MessageSquare,
  Loader2,
  ExternalLink,
  Lightbulb,
  HelpCircle,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const ReportIssuePage = () => {
  const { user } = useAuth();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState([]);
  
  const [newIssue, setNewIssue] = useState({
    title: '',
    description: '',
    category: 'bug',
    severity: 'medium',
    steps_to_reproduce: '',
    page_url: window.location.href
  });

  useEffect(() => {
    fetchIssues();
  }, []);

  const fetchIssues = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/issues`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIssues(response.data.issues || []);
    } catch (error) {
      console.error('Failed to fetch issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (issueId) => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/issues/${issueId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleSubmitIssue = async () => {
    if (!newIssue.title || !newIssue.description) {
      toast.error('Title and description are required');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/issues`, {
        ...newIssue,
        browser_info: navigator.userAgent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Issue reported! We\'ll look into it.');
      setShowReportDialog(false);
      setNewIssue({
        title: '',
        description: '',
        category: 'bug',
        severity: 'medium',
        steps_to_reproduce: '',
        page_url: window.location.href
      });
      fetchIssues();
    } catch (error) {
      toast.error('Failed to submit issue');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedIssue) return;

    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/issues/${selectedIssue.issue_id}/comment`, 
        { comment: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Comment added');
      setNewComment('');
      fetchComments(selectedIssue.issue_id);
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const openIssueDetails = (issue) => {
    setSelectedIssue(issue);
    fetchComments(issue.issue_id);
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-500/20 text-blue-400',
      in_progress: 'bg-yellow-500/20 text-yellow-400',
      resolved: 'bg-green-500/20 text-green-400',
      closed: 'bg-gray-500/20 text-gray-400',
      wont_fix: 'bg-red-500/20 text-red-400'
    };
    const labels = {
      new: 'New',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed',
      wont_fix: "Won't Fix"
    };
    return <Badge className={styles[status] || styles.new}>{labels[status] || status}</Badge>;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'bug': return <Bug className="w-4 h-4 text-red-400" />;
      case 'feature': return <Lightbulb className="w-4 h-4 text-yellow-400" />;
      case 'question': return <HelpCircle className="w-4 h-4 text-blue-400" />;
      default: return <MessageSquare className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <DashboardLayout title="Report Issue">
      <div className="px-4 py-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl text-white uppercase tracking-wider">
              Report an Issue
            </h1>
            <p className="text-aura-text-muted mt-1">
              Help us build a better system by reporting bugs or suggesting features
            </p>
          </div>
          
          <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
            <DialogTrigger asChild>
              <Button 
                className="bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider"
                data-testid="report-issue-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Report Issue
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                  Report an Issue
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Title *
                  </Label>
                  <Input
                    value={newIssue.title}
                    onChange={(e) => setNewIssue({ ...newIssue, title: e.target.value })}
                    placeholder="Brief summary of the issue"
                    className="h-12 bg-aura-bg border-white/10 text-white"
                    data-testid="issue-title-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                      Category
                    </Label>
                    <Select 
                      value={newIssue.category} 
                      onValueChange={(v) => setNewIssue({ ...newIssue, category: v })}
                    >
                      <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-aura-surface border-white/10">
                        <SelectItem value="bug" className="text-white">Bug Report</SelectItem>
                        <SelectItem value="feature" className="text-white">Feature Request</SelectItem>
                        <SelectItem value="question" className="text-white">Question</SelectItem>
                        <SelectItem value="other" className="text-white">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                      Severity
                    </Label>
                    <Select 
                      value={newIssue.severity} 
                      onValueChange={(v) => setNewIssue({ ...newIssue, severity: v })}
                    >
                      <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-aura-surface border-white/10">
                        <SelectItem value="low" className="text-white">Low</SelectItem>
                        <SelectItem value="medium" className="text-white">Medium</SelectItem>
                        <SelectItem value="high" className="text-white">High</SelectItem>
                        <SelectItem value="critical" className="text-white">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Description *
                  </Label>
                  <Textarea
                    value={newIssue.description}
                    onChange={(e) => setNewIssue({ ...newIssue, description: e.target.value })}
                    placeholder="Describe the issue in detail..."
                    className="bg-aura-bg border-white/10 text-white min-h-[100px]"
                    data-testid="issue-description-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Steps to Reproduce (for bugs)
                  </Label>
                  <Textarea
                    value={newIssue.steps_to_reproduce}
                    onChange={(e) => setNewIssue({ ...newIssue, steps_to_reproduce: e.target.value })}
                    placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                    className="bg-aura-bg border-white/10 text-white min-h-[80px]"
                  />
                </div>

                <Button
                  onClick={handleSubmitIssue}
                  disabled={submitting || !newIssue.title || !newIssue.description}
                  className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider h-12"
                  data-testid="submit-issue-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Submit Issue'
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Quick Tips */}
        <Card className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-white font-medium mb-1">Tips for helpful reports:</p>
                <ul className="text-aura-text-muted space-y-1">
                  <li>• Be specific - include what you expected vs what happened</li>
                  <li>• Include steps to reproduce the issue</li>
                  <li>• Mention which page/feature is affected</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Issues List */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Your Reports ({issues.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 bg-white/5 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : issues.length === 0 ? (
              <div className="text-center py-12">
                <Bug className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                <p className="text-aura-text-muted mb-2">No issues reported yet</p>
                <p className="text-sm text-aura-text-subtle">Found a bug? Let us know!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {issues.map((issue) => (
                  <button
                    key={issue.issue_id}
                    onClick={() => openIssueDetails(issue)}
                    className="w-full p-4 bg-white/5 rounded-lg border border-white/10 hover:border-white/20 transition-all text-left"
                    data-testid={`issue-${issue.issue_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        {getCategoryIcon(issue.category)}
                        <div>
                          <h3 className="font-medium text-white">{issue.title}</h3>
                          <p className="text-sm text-aura-text-muted line-clamp-1 mt-1">
                            {issue.description}
                          </p>
                          <p className="text-xs text-aura-text-subtle mt-2">
                            Reported {format(parseISO(issue.created_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(issue.status)}
                        <ChevronRight className="w-4 h-4 text-aura-text-muted" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Issue Detail Dialog */}
        <Dialog open={!!selectedIssue} onOpenChange={() => setSelectedIssue(null)}>
          <DialogContent className="bg-aura-surface border-white/10 max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            {selectedIssue && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(selectedIssue.category)}
                    <DialogTitle className="font-heading font-bold text-lg text-white">
                      {selectedIssue.title}
                    </DialogTitle>
                  </div>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  <div className="flex items-center gap-2">
                    {getStatusBadge(selectedIssue.status)}
                    <Badge className="bg-white/10 text-white">{selectedIssue.severity}</Badge>
                  </div>

                  <div className="p-3 bg-white/5 rounded-lg">
                    <p className="text-sm text-aura-text-muted whitespace-pre-wrap">
                      {selectedIssue.description}
                    </p>
                  </div>

                  {selectedIssue.steps_to_reproduce && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-aura-text-subtle mb-2">
                        Steps to Reproduce
                      </p>
                      <p className="text-sm text-aura-text-muted whitespace-pre-wrap">
                        {selectedIssue.steps_to_reproduce}
                      </p>
                    </div>
                  )}

                  {selectedIssue.resolution_notes && (
                    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-xs uppercase tracking-wider text-green-400 mb-1">
                        Resolution
                      </p>
                      <p className="text-sm text-white">
                        {selectedIssue.resolution_notes}
                      </p>
                    </div>
                  )}

                  {/* Comments */}
                  <div className="border-t border-white/10 pt-4">
                    <p className="text-xs uppercase tracking-wider text-aura-text-subtle mb-3">
                      Comments ({comments.length})
                    </p>
                    
                    {comments.length > 0 && (
                      <div className="space-y-2 mb-4 max-h-[200px] overflow-y-auto">
                        {comments.map((comment) => (
                          <div key={comment.comment_id} className="p-2 bg-white/5 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-white">{comment.user_name}</span>
                              <span className="text-xs text-aura-text-subtle">
                                {format(parseISO(comment.created_at), 'MMM d, h:mm a')}
                              </span>
                            </div>
                            <p className="text-sm text-aura-text-muted">{comment.comment}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Input
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        className="bg-aura-bg border-white/10 text-white"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                      />
                      <Button
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                        className="bg-aura-primary hover:bg-aura-primary-hover"
                      >
                        Send
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ReportIssuePage;
